<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-card elevation="0" outlined class="mt-5">
        <v-sheet class="pa-4 d-md-flex">
          <v-sheet class="flex-sm-column">
            <p class="text-h4 ma-0">Users</p>
          </v-sheet>

          <v-sheet class="flex-sm-column my-3 my-md-0">
            <v-breadcrumbs class="pa-md-3 pa-0">
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'home', params: {} }"
                >Home</v-breadcrumbs-item
              >
              <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'user', params: {} }"
                active
              >
                User
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-sheet>

          <v-spacer></v-spacer>

          <v-sheet class="flex-sm-column">
            <v-row class="flex">
              <v-col cols="12" md="8">
                <v-text-field
                  dense
                  v-model="searchTerms"
                  @keyup.enter="search"
                  @click:clear="clearSearch"
                  rounded
                  @click:append="search"
                  placeholder="Search User"
                  append-icon="mdi-magnify"
                  outlined
                  clearable
                  hide-details
                  label="Search"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                  class="align-self-end align-self-lg-start text-capitalize"
                  color="primary"
                  block
                  text
                  outlined
                  @click="userDialog = true"
                >
                  <v-icon>mdi-plus</v-icon>
                  Create User
                </v-btn>
              </v-col>
            </v-row>
          </v-sheet>
        </v-sheet>
      </v-card>

      <v-card elevation="0" class="mt-5">
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="userList"
            :items-per-page="pagination.pageSize"
            :loading="!userProcessed"
            :server-items-length="pagination.totalSize"
            :page="pagination.current"
            :disable-pagination="!userList || userList.length < 1"
            :hide-default-header="!userList || userList.length < 1"
            :hide-default-footer="!userList || userList.length < 1"
            :disable-sort="true"
            :footer-props="{
              'items-per-page-options': [5, 10, 15, 25, 50, 100, -1],
              'items-per-page-text': 'Rows per page:',
              'items-per-page-all-text': 'All',
            }"
            item-key="id"
            class="elevation-1"
            @update:options="pageChanged"
          >
            <template v-slot:item.firstName="{ item }">
              <a @click="view(item)"
                >{{ item.firstName }} {{ item.lastName }}</a
              >
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon class="mr-2" @click="edit(item)"> mdi-pencil </v-icon>
              <v-icon color="red" class="mr-2" @click="remove(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <confirmation-dialog ref="confirm" />

    <v-row justify="center">
      <v-dialog v-model="userDialog" persistent max-width="800px" scrollable>
        <user-form
          :key="userId"
          :id="userId"
          :isModal="true"
          @closeUserDialog="closeUserDialog"
        ></user-form>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog v-model="viewUserDialog" persistent max-width="600px">
        <v-card elevation="0">
          <v-card-text elevation="0" class="pa-0">
            <view-user
              v-if="userId"
              :key="userId"
              :userId="userId"
              :isModal="true"
              @closeUserDialog="closeUserDialog"
              @editUserDialog="editFromView"
            ></view-user>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
const UserForm = () => import("@/components/user/UserFormNew.vue");
const ViewUser = () => import("@/components/user/UserView.vue");

export default {
  data() {
    return {
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 0,
      },
      userList: [],
      showParentPage: true,
      headers: [
        {
          text: "Name",
          value: "firstName",
          align: "start",
          sortable: true,
        },
        {
          text: "User Name",
          value: "username",
          align: "start",
          sortable: true,
        },
        {
          text: "Phone",
          value: "phone",
          align: "start",
          sortable: true,
        },
        {
          text: "Email",
          value: "email",
          align: "start",
          sortable: true,
        },
        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
      sort: {
        key: null,
      },
      searchTerms: "",
      userDialog: false,
      viewUserDialog: false,
      userId: null,
      openResetPasswordDialog: false,
    };
  },
  components: {
    ConfirmationDialog,
    UserForm,
    ViewUser,
  },
  computed: {
    ...mapGetters("user", ["userProcessed", "getUsers"]),
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("user", ["deleteUser", "fetchUsers"]),

    view(item) {
      this.viewUserDialog = true;
      this.userId = item.id;
    },

    editFromView() {
      this.viewUserDialog = false;
      this.userDialog = true;
    },

    edit(item) {
      this.userId = item.id;
      this.userDialog = true;
    },

    async remove(item) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this user?",
      );
      if (confirm) {
        this.deleteUser(item.id)
          .then(() => {
            this.addSuccessMessage("User was deleted successfully!");
            this.getResources();
          })
          .catch((error) => {
            this.addErrorMessage("Server response with error: " + error);
          });
      }
    },

    getResources() {
      // Handle "Show All" case
      let pageSize = this.pagination.pageSize;
      let page = this.pagination.current > 0 ? this.pagination.current - 1 : 0;

      if (pageSize === -1) {
        pageSize =
          this.pagination.totalSize > 0 ? this.pagination.totalSize : 10000;
        page = 0;
      }

      const params = {
        page: page,
        size: pageSize,
        searchTerms: this.searchTerms || "",
      };

      this.fetchUsers(params)
        .then((response) => {
          if (response) {
            this.pagination.totalSize = response.totalElements;
            this.userList = response.content;
          }
        })
        .catch((error) => {
          console.error("Error fetching users:", error);
          this.addErrorMessage("Server response with error: " + error);
        });
    },

    showParent(show) {
      this.showParentPage = show;
    },

    pageChanged(options) {
      // Update pagination state
      this.pagination.current = options.page;
      this.pagination.pageSize = options.itemsPerPage;

      // Fetch new data
      this.getResources();
    },

    search() {
      // Reset to first page when searching
      this.pagination.current = 1;
      this.getResources();
    },

    clearSearch() {
      this.searchTerms = "";
      // Reset to first page when clearing search
      this.pagination.current = 1;
      this.getResources();
    },

    closeUserDialog() {
      this.userDialog = false;
      this.viewUserDialog = false;
      this.userId = null;
      this.getResources();
    },
  },

  watch: {
    // "pagination.numberRegisterForPage": function() {
    //   this.pagination.current = -1;
    // },
  },

  mounted() {
    // Load initial data when component is mounted
    this.getResources();
  },

  beforeRouteUpdate(to, from, next) {
    const toDepth = to.path.split("/").length;
    const fromDepth = from.path.split("/").length;
    this.showParentPage = toDepth < fromDepth;
    if (this.showParentPage) {
      this.getResources();
    }
    next();
  },
};
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}
</style>
